import styled from "styled-components";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ReceiptId, StoreId } from "@KassaroAB/shared/types/ids";
import type { DBDiscount } from "@KassaroAB/shared/types/discount";
import type { RestReceipt } from "@KassaroAB/shared/types/receipt";
import type { RestCartItem } from "@KassaroAB/shared/types/cartItem";
import { ReceiptType } from "@KassaroAB/shared/types/enums/receiptType";
import { ProductType } from "@KassaroAB/shared/types/enums/ProductType";
import { DiscountType } from "@KassaroAB/shared/types/enums/DiscountType";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import {
  calculateTotalDiscounts,
  calculateTotalVat,
  calculateVatByRate,
  convertReceiptToLineItems,
  getReceiptLevelDiscountBreakdown,
} from "@KassaroAB/shared/utils/receiptCalculations";
import { multiplyMoney, subtractMoney } from "@KassaroAB/shared/utils/money";
import { isModifierInternal } from "@KassaroAB/shared/utils/takeawayCartLine";
import { useGetReceiptQuery } from "../../../query/receiptsQuery";
import { formatDateTime } from "../../../utils/dateUtils";
import { formatMoney } from "../../../utils/formatMoney";
import { LoadingContainer } from "../../../components/LoadingContainer";
import { SectionTitle } from "../../../components/SectionTitle";
import { ReceiptPayment } from "./ReceiptPayment";
import { ReceiptFiscalizationInfo } from "./ReceiptFiscalizationInfo";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${colors.border};
  margin: 0;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${spacing.x05}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

const MetaLabel = styled.span`
  color: ${colors.textMuted};
`;

const MetaValue = styled.span`
  color: ${colors.text};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
`;

const TotalRow = styled(Row)`
  font-weight: 600;
`;

const ItemBlock = styled.div`
  padding-bottom: ${spacing.x2}px;

  &:last-child {
    padding-bottom: 0;
  }
`;

const ItemName = styled.div`
  font-weight: 600;
  color: ${colors.text};
`;

const ItemSubtext = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const ModifierRow = styled(Row)`
  margin-top: ${spacing.x05}px;
  padding-left: ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

const LinkedReceiptLink = styled(Link)`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  background-color: ${colors.backgroundLight};
  cursor: pointer;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  text-decoration: none;

  &:hover {
    border-color: ${colors.primary};
    background-color: ${colors.background};
  }
`;

const FooterText = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  text-align: center;
  padding-top: ${spacing.x1}px;
`;

const CompanyDisplayName = styled.div`
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: ${spacing.x05}px;
`;

const CenteredText = styled.p`
  margin: 0;
  text-align: center;
  color: ${colors.textMuted};
  font-size: ${fontSizes.regular}px;
`;

const formatDiscountLabel = (discount: DBDiscount): string => {
  if (discount.type === DiscountType.PERCENTAGE) {
    return `${discount.percentage}%`;
  }
  return formatMoney(discount.amount);
};

const formatLineDiscountLabel = (
  item: RestCartItem,
  discountWord: string,
): string => {
  if (item.appliedOffer) {
    return item.appliedOffer.receiptText?.trim() || item.appliedOffer.name;
  }
  if (!item.discount) {
    return "";
  }
  return `${discountWord}: ${formatDiscountLabel(item.discount)}`;
};

const formatDiscountMoney = (amount: RestCartItem["unitPrice"]): string => {
  return formatMoney(multiplyMoney(amount, -1));
};

const formatQuantityLabel = (item: RestCartItem): string => {
  if (item.parts != null && item.parts >= 2 && item.parts <= 999) {
    const numerator = Math.round(item.sizeOfItem * item.parts);
    if (numerator >= 1) {
      return `${item.quantity * numerator}/${item.parts}`;
    }
  }
  if (item.unit) {
    return `${item.quantity} × ${item.sizeOfItem.toFixed(item.unit.decimals)} ${item.unit.suffix ?? "pcs"}`;
  }
  if (item.sizeOfItem !== 1) {
    return `${item.quantity} × ${item.sizeOfItem}`;
  }
  return `${item.quantity}`;
};

interface LinkedReceiptItemProps {
  storeId: StoreId;
  receiptId: ReceiptId;
  label: string;
}

const LinkedReceiptItem = ({
  storeId,
  receiptId,
  label,
}: LinkedReceiptItemProps) => {
  const { data: linkedReceipt } = useGetReceiptQuery(storeId, receiptId);

  if (!linkedReceipt) {
    return null;
  }

  return (
    <LinkedReceiptLink to={`/stores/${storeId}/receipts/${linkedReceipt.id}`}>
      <span>
        {label} #{linkedReceipt.receiptNumber}
      </span>
      <span>{formatMoney(linkedReceipt.total)}</span>
    </LinkedReceiptLink>
  );
};

interface ReceiptDetailPanelProps {
  storeId: StoreId;
  receiptId: ReceiptId;
  currency: Currency;
  showArticleNumber?: boolean;
  showReferenceOnReceipt?: boolean;
  showStaffOnReceipt?: boolean;
  showDisplayNameOnReceipt?: boolean;
  receipt?: RestReceipt;
  readOnly?: boolean;
}

export const ReceiptDetailPanel = ({
  storeId,
  receiptId,
  currency,
  showArticleNumber = false,
  showReferenceOnReceipt = false,
  showStaffOnReceipt = false,
  showDisplayNameOnReceipt = false,
  receipt: receiptOverride,
  readOnly = false,
}: ReceiptDetailPanelProps) => {
  const { t } = useTranslation();
  const { data: fetchedReceipt, isLoading, error } = useGetReceiptQuery(
    storeId,
    receiptOverride ? null : receiptId,
  );
  const receipt = receiptOverride ?? fetchedReceipt;

  if (!receiptOverride && isLoading) {
    return <LoadingContainer message={t("receiptsView.loadingDetail")} />;
  }

  if (error || !receipt) {
    return <CenteredText>{t("receiptsView.errorDetail")}</CenteredText>;
  }

  const lineItems = convertReceiptToLineItems(receipt, currency);
  const totalVat = calculateTotalVat(lineItems, currency);
  const totalVatByPercentage = calculateVatByRate(lineItems);
  const cartDiscountBreakdown = getReceiptLevelDiscountBreakdown(
    receipt,
    currency,
  );
  const isRefund = receipt.type === ReceiptType.REFUND;
  const prepTicketReference =
    showReferenceOnReceipt && receipt.prepTicketReference?.trim()
      ? receipt.prepTicketReference.trim()
      : undefined;
  const staffName =
    showStaffOnReceipt && receipt.staffName?.trim()
      ? receipt.staffName.trim()
      : undefined;
  const displayName =
    showDisplayNameOnReceipt && receipt.company.displayName?.trim()
      ? receipt.company.displayName.trim()
      : undefined;

  return (
    <DetailContent>
      {(prepTicketReference || staffName) && (
        <MetaGrid>
          {prepTicketReference && (
            <>
              <MetaLabel>{t("receiptsView.detail.prepTicketReference")}</MetaLabel>
              <MetaValue>{prepTicketReference}</MetaValue>
            </>
          )}
          {staffName && (
            <>
              <MetaLabel>{t("receiptsView.detail.staff")}</MetaLabel>
              <MetaValue>{staffName}</MetaValue>
            </>
          )}
        </MetaGrid>
      )}

      <Section>
        {displayName && <CompanyDisplayName>{displayName}</CompanyDisplayName>}
        <SectionTitle>{receipt.company.name}</SectionTitle>
        <MetaGrid>
          <MetaLabel>{t("receiptsView.detail.companyOrgNumber")}</MetaLabel>
          <MetaValue>{receipt.company.orgNumber}</MetaValue>
          <MetaLabel>{t("receiptsView.detail.companyAddress")}</MetaLabel>
          <MetaValue>
            {receipt.company.street}, {receipt.company.postalcode} {receipt.company.city}
          </MetaValue>
          <MetaLabel>{t("receiptsView.detail.createdAt")}</MetaLabel>
          <MetaValue>{formatDateTime(receipt.createdAt)}</MetaValue>
        </MetaGrid>
      </Section>

      <Divider />

      <Section>
        {receipt.items.map((item) => {
          if (item.type === ProductType.TEXT) {
            return (
              <ItemBlock key={item.id}>
                <ItemName>{item.name}</ItemName>
              </ItemBlock>
            );
          }
          const total = multiplyMoney(item.unitPrice, item.quantity * item.sizeOfItem);
          const itemDiscountAmount = item.discount
            ? calculateTotalDiscounts(
                convertReceiptToLineItems({ items: [item] }, currency),
                currency,
              )
            : null;
          return (
            <ItemBlock key={item.id}>
              <Row>
                <ItemName>{item.name}</ItemName>
                <span>{formatMoney(total)}</span>
              </Row>
              <ItemSubtext>
                {formatQuantityLabel(item)} × {formatMoney(item.unitPrice)}
              </ItemSubtext>
              {showArticleNumber && item.articleNumber && item.articleNumber !== "" && (
                <ItemSubtext>{item.articleNumber}</ItemSubtext>
              )}
              {item.variantName?.trim() && (
                <ItemSubtext>{item.variantName.trim()}</ItemSubtext>
              )}
              {item.extraFields && item.extraFields.length > 0 && (
                <ItemSubtext>
                  {item.extraFields.map((field) => `${field.name}: ${field.value}`).join(", ")}
                </ItemSubtext>
              )}
              {item.modifiers
                ?.filter((modifier) => !isModifierInternal(modifier))
                .map((modifier) => (
                  <ModifierRow key={modifier.modifierId}>
                    <span>
                      {modifier.removed
                        ? `- ${modifier.name}`
                        : (modifier.quantity ?? 1) > 1
                          ? `${modifier.quantity} x ${modifier.name}`
                          : modifier.name}
                    </span>
                    {modifier.unitPrice.amount !== 0 && (
                      <span>
                        {formatMoney(
                          multiplyMoney(
                            modifier.unitPrice,
                            item.quantity * item.sizeOfItem * (modifier.quantity ?? 1),
                          ),
                        )}
                      </span>
                    )}
                  </ModifierRow>
                ))}
              {item.receiptNote && (
                <ModifierRow>
                  <span>{item.receiptNote}</span>
                </ModifierRow>
              )}
              {(item.discount || item.appliedOffer) && (
                <ModifierRow>
                  <span>
                    {formatLineDiscountLabel(item, t("receiptsView.detail.discount"))}
                  </span>
                  {itemDiscountAmount ? (
                    <span>{formatDiscountMoney(itemDiscountAmount)}</span>
                  ) : null}
                </ModifierRow>
              )}
            </ItemBlock>
          );
        })}
      </Section>

      <Divider />

      <Section>
        {cartDiscountBreakdown && receipt.discount ? (
          <>
            <Row>
              <span>{t("receiptsView.detail.subtotal")}</span>
              <span>
                {formatMoney(cartDiscountBreakdown.subtotalBeforeCartDiscount)}
              </span>
            </Row>
            <Row>
              <span>
                {t("receiptsView.detail.discount")}:{" "}
                {formatDiscountLabel(receipt.discount)}
              </span>
              <span>
                {formatDiscountMoney(
                  cartDiscountBreakdown.receiptLevelDiscountAmount,
                )}
              </span>
            </Row>
          </>
        ) : null}
        {receipt.rounding && receipt.rounding.amount !== 0 && (
          <Row>
            <span>{t("receiptsView.detail.rounding")}</span>
            <span>{formatMoney(receipt.rounding)}</span>
          </Row>
        )}
        <TotalRow>
          <span>{t("receiptsView.detail.total")}</span>
          <span>{formatMoney(receipt.total)}</span>
        </TotalRow>
        <Row>
          <span>{t("receiptsView.detail.vatIncluded")}</span>
          <span>{formatMoney(totalVat)}</span>
        </Row>
        {Array.from(totalVatByPercentage.entries()).map(([percentage, amounts]) => {
          if (amounts.vatAmount.amount === 0) {
            return null;
          }
          return (
            <Row key={percentage}>
              <span>{t("receiptsView.detail.vatPercentage", { percentage })}</span>
              <span>{formatMoney(amounts.vatAmount)}</span>
            </Row>
          );
        })}
        <Row>
          <span>{t("receiptsView.detail.net")}</span>
          <span>{formatMoney(subtractMoney(receipt.total, totalVat))}</span>
        </Row>
        {receipt.tip && receipt.tip.amount !== 0 && (
          <Row>
            <span>{t("receiptsView.detail.tip")}</span>
            <span>{formatMoney(receipt.tip)}</span>
          </Row>
        )}
      </Section>

      {receipt.payments.length > 0 && (
        <>
          <Divider />
          <Section>
            <SectionTitle>{t("receiptsView.detail.payments")}</SectionTitle>
            {receipt.payments.map((payment) => (
              <ReceiptPayment
                key={`${payment.paymentMethodId}-${payment.paidAt}`}
                payment={payment}
              />
            ))}
          </Section>
        </>
      )}

      <ReceiptFiscalizationInfo receipt={receipt} />

      {receipt.purchaseNoteInfo && (
        <>
          <Divider />
          <Section>
            <SectionTitle>{t("receiptsView.detail.purchaseNote")}</SectionTitle>
            <MetaGrid>
              <MetaLabel>{t("receiptsView.detail.purchaseNoteName")}</MetaLabel>
              <MetaValue>{receipt.purchaseNoteInfo.name}</MetaValue>
              <MetaLabel>{t("receiptsView.detail.purchaseNotePhone")}</MetaLabel>
              <MetaValue>{receipt.purchaseNoteInfo.phone}</MetaValue>
              <MetaLabel>{t("receiptsView.detail.purchaseNoteSsn")}</MetaLabel>
              <MetaValue>{receipt.purchaseNoteInfo.ssn}</MetaValue>
              <MetaLabel>{t("receiptsView.detail.purchaseNoteProductInfo")}</MetaLabel>
              <MetaValue>{receipt.purchaseNoteInfo.productInfo}</MetaValue>
              <MetaLabel>{t("receiptsView.detail.purchaseNoteAddress")}</MetaLabel>
              <MetaValue>{receipt.purchaseNoteInfo.address}</MetaValue>
              <MetaLabel>{t("receiptsView.detail.purchaseNoteIdType")}</MetaLabel>
              <MetaValue>{receipt.purchaseNoteInfo.idType}</MetaValue>
            </MetaGrid>
          </Section>
        </>
      )}

      {receipt.customer && (
        <>
          <Divider />
          <Section>
            <SectionTitle>{t("receiptsView.detail.customer")}</SectionTitle>
            <MetaGrid>
              {receipt.customer.name && (
                <>
                  <MetaLabel>{t("receiptsView.detail.customerName")}</MetaLabel>
                  <MetaValue>{receipt.customer.name}</MetaValue>
                </>
              )}
              {receipt.customer.email && (
                <>
                  <MetaLabel>{t("receiptsView.detail.customerEmail")}</MetaLabel>
                  <MetaValue>{receipt.customer.email}</MetaValue>
                </>
              )}
              {receipt.customer.phone && (
                <>
                  <MetaLabel>{t("receiptsView.detail.customerPhone")}</MetaLabel>
                  <MetaValue>{receipt.customer.phone}</MetaValue>
                </>
              )}
              {receipt.customer.note && (
                <>
                  <MetaLabel>{t("receiptsView.detail.customerNote")}</MetaLabel>
                  <MetaValue>{receipt.customer.note}</MetaValue>
                </>
              )}
            </MetaGrid>
          </Section>
        </>
      )}

      {isRefund && receipt.originalReceiptId && !readOnly && (
        <>
          <Divider />
          <Section>
            <SectionTitle>{t("receiptsView.detail.originalReceipt")}</SectionTitle>
            <LinkedReceiptItem
              storeId={storeId}
              receiptId={receipt.originalReceiptId}
              label={t("receiptsView.detail.original")}
            />
          </Section>
        </>
      )}

      {receipt.refundReceipts && receipt.refundReceipts.length > 0 && !readOnly && (
        <>
          <Divider />
          <Section>
            <SectionTitle>
              {t("receiptsView.detail.refundReceiptsCount", {
                count: receipt.refundReceipts.length,
              })}
            </SectionTitle>
            {receipt.refundReceipts.map((refundReceiptId) => (
              <LinkedReceiptItem
                key={refundReceiptId}
                storeId={storeId}
                receiptId={refundReceiptId}
                label={t("receiptsView.detail.refund")}
              />
            ))}
          </Section>
        </>
      )}

      <Divider />
      <FooterText>{t("receiptsView.detail.paidVia", { brand: "Kassaro" })}</FooterText>
    </DetailContent>
  );
};
